import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, fmtEuros, StatusBadge } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle2, XCircle, Paperclip, Inbox, ArrowLeft, HandCoins } from "lucide-react";

export default function ValidatorPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [report, setReport] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const loadQueue = useCallback(() => {
    api.get("/expense-reports", { params: { status: "PENDING_APPROVAL", for_validation: true, page_size: 50 } })
      .then(({ data }) => setQueue(data.reports))
      .catch(() => setQueue([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  useEffect(() => {
    if (reportId) {
      api.get(`/expense-reports/${reportId}`)
        .then(({ data }) => {
          setReport(data.report);
          const init = {};
          data.report.expenses.forEach((e) => { init[e.id] = { decision: "approved", reason: "" }; });
          setDecisions(init);
        })
        .catch((e) => { toast.error(formatApiError(e)); navigate("/validation"); });
    } else {
      setReport(null);
    }
  }, [reportId, navigate]);

  const filtered = queue.filter((r) =>
    !search || r.number.toLowerCase().includes(search.toLowerCase()) ||
    (r.requester_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const setDecision = (id, decision) => setDecisions((p) => ({ ...p, [id]: { ...p[id], decision } }));
  const setReason = (id, reason) => setDecisions((p) => ({ ...p, [id]: { ...p[id], reason } }));

  const openAttachment = async (att) => {
    try {
      const res = await api.get(`/attachments/${att.id}/preview`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rejectedCount = Object.values(decisions).filter((d) => d.decision === "rejected").length;
  const missingReason = Object.values(decisions).some((d) => d.decision === "rejected" && !d.reason.trim());

  const submitDecision = async () => {
    if (missingReason) return toast.error("Un motif est obligatoire pour chaque dépense refusée.");
    const label = rejectedCount === 0 ? "valider entièrement" : rejectedCount === report.expenses.length ? "refuser entièrement" : "valider partiellement";
    if (!window.confirm(`Confirmer : ${label} cette note ? Cette décision est définitive.`)) return;
    setSending(true);
    try {
      const items = Object.entries(decisions).map(([expense_id, d]) => ({
        expense_id, decision: d.decision, reason: d.decision === "rejected" ? d.reason.trim() : null,
      }));
      await api.post(`/expense-reports/${report.id}/decision`, { items });
      toast.success("Décision enregistrée et notifiée au demandeur.");
      setReport(null);
      navigate("/validation");
      loadQueue();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  if (report) {
    const approvedTotal = report.expenses.reduce((s, e) => s + (decisions[e.id]?.decision === "approved" ? e.amount_cents : 0), 0);
    return (
      <div className="space-y-6 animate-enter" data-testid="validator-detail">
        <Link to="/validation" className="text-[#0369a1] font-semibold inline-flex items-center gap-2" data-testid="validator-back-link">
          <ArrowLeft className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Retour à la file d'attente
        </Link>
        <div className="card-base">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>{report.number}</h1>
              <p className="text-lg text-slate-600 mt-1">{report.requester_name} — période {report.period}</p>
            </div>
            <p className="text-2xl font-bold text-[#0369a1]">{fmtEuros(report.total_requested_cents)}</p>
          </div>
        </div>

        <div className="space-y-4">
          {report.expenses.map((e) => {
            const d = decisions[e.id] || { decision: "approved", reason: "" };
            return (
              <div key={e.id} className={`card-base !p-5 border-2 transition-colors ${d.decision === "rejected" ? "!border-red-300 !bg-red-50/40" : "!border-green-200"}`}
                data-testid={`decision-expense-${e.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-60">
                    <p className="font-bold text-lg text-slate-900">{e.category_label}</p>
                    <p className="text-slate-600">{e.date} — {e.purpose}</p>
                    {e.is_donation && (
                      <p className="inline-flex items-center gap-1 mt-1 px-2 py-1 rounded-lg bg-rose-100 text-rose-800 font-semibold text-sm" data-testid={`validator-donation-${e.id}`}>
                        <HandCoins className="h-4 w-4" aria-hidden="true" /> Abandonnée (transfert au denier) — non remboursée si acceptée
                      </p>
                    )}
                    {(e.attachments || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {e.attachments.map((a) => (
                          <button key={a.id} onClick={() => openAttachment(a)}
                            className="inline-flex items-center gap-2 px-3 h-10 rounded-lg border-2 border-slate-300 text-[#0369a1] font-semibold hover:bg-sky-50"
                            data-testid={`validator-attachment-${a.id}`}>
                            <Paperclip className="h-4 w-4" aria-hidden="true" /> {a.filename}
                          </button>
                        ))}
                      </div>
                    )}
                    {(e.attachments || []).length === 0 && <p className="text-amber-700 font-medium mt-2">Aucun justificatif joint</p>}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-2xl text-slate-900">{fmtEuros(e.amount_cents)}</p>
                  </div>
                </div>
                <div className="flex gap-3 mt-4 flex-col sm:flex-row">
                  <button onClick={() => setDecision(e.id, "approved")}
                    className={`flex-1 h-14 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                      d.decision === "approved" ? "bg-[#15803d] text-white" : "bg-white text-[#15803d] border-2 border-[#15803d]"
                    }`} data-testid={`approve-expense-${e.id}`}>
                    <CheckCircle2 className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Accepter
                  </button>
                  <button onClick={() => setDecision(e.id, "rejected")}
                    className={`flex-1 h-14 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                      d.decision === "rejected" ? "bg-[#b91c1c] text-white" : "bg-white text-[#b91c1c] border-2 border-[#b91c1c]"
                    }`} data-testid={`reject-expense-${e.id}`}>
                    <XCircle className="h-5 w-5" aria-hidden="true" strokeWidth={2.5} /> Refuser
                  </button>
                </div>
                {d.decision === "rejected" && (
                  <div className="mt-3">
                    <Textarea value={d.reason} onChange={(ev) => setReason(e.id, ev.target.value)}
                      placeholder="Motif du refus (obligatoire) — ex : justificatif illisible, dépense non conforme…"
                      className="text-lg border-2 border-red-300 rounded-xl px-4 py-3 min-h-20 w-full bg-white"
                      data-testid={`reject-reason-${e.id}`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="card-base space-y-4 sticky bottom-4 border-2 !border-slate-300 shadow-lg">
          <div className="flex justify-between text-lg font-semibold flex-wrap gap-2">
            <span className="text-[#15803d]">Accepté : {fmtEuros(approvedTotal)}</span>
            <span className="text-[#b91c1c]">Refusé : {fmtEuros(report.total_requested_cents - approvedTotal)}</span>
          </div>
          <button onClick={submitDecision} disabled={sending || missingReason} className="btn-primary-lg w-full" data-testid="submit-decision-button">
            {sending ? "Enregistrement…" : "Enregistrer ma décision"}
          </button>
          {missingReason && <p className="text-[#b91c1c] font-semibold text-center">Saisissez un motif pour chaque dépense refusée.</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-enter" data-testid="validator-queue">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Work Sans" }}>Demandes à valider</h1>
          <p className="text-lg text-slate-600 mt-1">Notes de frais de vos périmètres en attente de décision.</p>
        </div>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (numéro, nom)…"
          className="input-lg !w-72" data-testid="validator-search-input" />
      </div>

      {loading ? (
        <p className="text-lg text-slate-600">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="card-base text-center py-12" data-testid="empty-queue">
          <Inbox className="h-16 w-16 text-slate-300 mx-auto mb-4" aria-hidden="true" strokeWidth={2.5} />
          <p className="text-xl font-semibold text-slate-700">Aucune demande en attente</p>
          <p className="text-slate-500 mt-2">Les nouvelles notes soumises apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <button key={r.id} onClick={() => navigate(`/validation/${r.id}`)}
              className="w-full text-left card-base !p-5 hover:border-[#0369a1] border-2 transition-colors"
              data-testid={`queue-report-${r.id}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-bold text-lg text-slate-900">{r.number} — {r.requester_name}</p>
                  <p className="text-slate-600">Période {r.period} — soumise le {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("fr-FR") : "—"}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-2xl text-[#0369a1]">{fmtEuros(r.total_requested_cents)}</p>
                  <StatusBadge status={r.status} label={r.status_label} />
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
